import { values, forOwn, isEmpty, uniq, keys, isArray } from "lodash";
import db from "../data/megamixWithSubstanceIds.json";
import { searchThrough } from './megamixUtils'

export const NO_REACTION = "NO_REACTION";

export function getColors(filters) {
  filters = filters === undefined ? {} : filters;

  if (filters.simple !== undefined) {
    return values(db.colors).filter(color => color.simple === filters.simple);
  } else {
    return values(db.colors);
  }
}

export function getColor(id) {
  return db.colors[id];
}

export const searchColors = (match) => (
  searchThrough(getColors(), match)
);

export function getReagents() {
  return values(db.reagents);
}

export function getReagent(id) {
  return db.reagents[id];
}

export const searchReagents = (match) => (
  searchThrough(getReagents(), match)
);

export function getSubstances() {
  return values(db.substances);
}

export function getSubstance(id) {
  return db.substances[id];
}

export function getSubstanceByToken(token) {
  for (const [substanceId, substance] of Object.entries(db.substances)) {
    if (substance["token"] === token)
      return substance;
  }
  return null;
}

export function getDefaultReagentId() {
  return getReagents()[0].id;
}

export function getSubstanceReagents(substanceId) {
  let data = [];
  forOwn(db.results[substanceId], (reactions, reagentId) => {
    data.push({
      reagent: getReagent(reagentId),
      reactions: reactions.map(reaction => ({
        colors: reaction[0].map(getColor),
        isReacting: reaction[2],
        hint: reaction[3]
      })),
    });
  });
  return data;
}

export function findReagentPresentColorIds(isSimpleQuery) {
  let data = {};
  keys(db.reagents).forEach(reagentId => {
    data[reagentId] = findPresentColorIds(reagentId, isSimpleQuery);
  });
  return data;
}

export function findPresentColorIds(reagentId, isSimpleQuery) {
  return computePresentColorIds(db.results, reagentId, isSimpleQuery);
}

export function computePresentColorIds(resultsHash, reagentId, isSimpleQuery) {
  let presentColors = [];
  forOwn(resultsHash, (substanceHash, _resultSubstanceId) => {
    forOwn(substanceHash, (reactions, resultReagentId) => {
      reactions.forEach(reaction => {
        if (resultReagentId === reagentId) {
          const colorIds = isSimpleQuery ? reaction[1] : reaction[0];
          colorIds.forEach(colorId => presentColors.push(colorId));
        }
      });
    });
  });
  return uniq(presentColors);
}

export function arrayQueriesToObjectQueries(arrayQueries) {
  var objectQueries = {};
  arrayQueries.forEach(query => {
    if (query != null) {
      objectQueries[query.reagentId] = query.colorIds;
    }
  });
  return objectQueries;
}

//
// queries = [
//    {reagentId: 10, colorIds: [2, 3]},
//    {reagentId: 5, colorIds: [3, 5, 6]}
// ]
//
export function findReactionsFromQueries(
  arrayQueries,
  simple,
  limitedSubstanceIds
) {
  const queries = arrayQueriesToObjectQueries(arrayQueries);
  return findReactions(queries, simple, limitedSubstanceIds);
}

//
// queries = [
//    {reagentId: 10, colorIds: [2, 3]},
//    {reagentId: 5, colorIds: [3, 5, 6]}
// ]
//
export function countReactionsFromQueries(
  queries,
  simple,
  limitedSubstanceIds
) {
  return computeReactions(
    db.results,
    arrayQueriesToObjectQueries(queries),
    simple === true,
    limitedSubstanceIds
  ).length;
}

export function anyReactionsFromQueriesForReagent(
  queries,
  simple,
  limitedSubstanceIds,
  reagentId
) {
  const reactions = computeReactions(
    db.results,
    arrayQueriesToObjectQueries(queries),
    simple === true,
    limitedSubstanceIds
  );
  for (let i = 0; i < reactions.length; i++) {
    for (let j = 0; j < reactions[i].results.length; j++) {
      if (reactions[i].results[j].reagentId == reagentId) {
        return true;
      }
    }
  }

  return false;
}

//
// queries = {
//    reagentId1: [colorId1, colorId2],
//    reagentId2: ['NO_REACTION']
// }
// simple = true | false
//
export function findReactions(queries, simple, limitedSubstanceIds) {
  const reactions = computeReactions(
    db.results,
    queries,
    simple === true,
    limitedSubstanceIds
  );
  return renderReactions(reactions);
}

// {
//   subsanceId: '1',
//   results: [
//     { reagentId: '1', colorIds: ['5', '6'], isReacting: true, hint: 'looks cool' },
//     { reagentId: '1', colorIds: ['1', '2'], isReacting: true, hint: '' },
//     { reagentId: '2', colorIds: [], isReacting: false, hint: '' }
//   ]
// }
export const renderReactions = reactions => (
  reactions.map(reaction => ({
    substance: getSubstance(reaction.substanceId),
    results: reaction.results.map(result => ({
      reagent: getReagent(result.reagentId),
      colors: renderResultColors(
        result.colorIds,
        getReagent(result.reagentId)
      ),
      isReacting: result.isReacting,
      hint: result.hint,
    })),
  }))
);

const whiteColor = getColors().find(color => color.name === "black1");

function renderResultColors(colorIds, reagent) {
  let colors = [];
  if (reagent.whiteFirstColor) {
    colors.push(whiteColor);
  }
  return colors.concat(colorIds.map(colorId => db.colors[colorId]));
}

export function computeReactions(
  resultsNormalized,
  queries,
  isSimpleQuery,
  limitedSubstanceIds
) {
  let found = [];

  // Each Substance
  forOwn(resultsNormalized, (substanceReagentResults, substanceId) => {
    // Limit checked substances to only those in limitedSubstanceIds
    if (
      isArray(limitedSubstanceIds) &&
      !limitedSubstanceIds.includes(substanceId)
    ) {
      return;
    }

    // Each reagent must me matched positively
    let anyReagentMatchedFalse = false;
    let matchingResults = [];

    // Each Reagent
    forOwn(substanceReagentResults, (results, reagentId) => {
      if (isEmpty(queries[reagentId])) {
        return;
      }

      // Each result
      let anyResultMatchedFalse = false;
      results.forEach(result => {
        if (
          isResultMatching(result, queries[reagentId], isSimpleQuery) === false
        ) {
          anyResultMatchedFalse = true;
        } else {
          matchingResults.push({
            reagentId,
            colorIds: result[0],
            isReacting: result[2],
            hint: result[3]
          });
        }
      });

      if (anyResultMatchedFalse) {
        anyReagentMatchedFalse = true;
      }
    });

    if (anyReagentMatchedFalse === false && matchingResults.length > 0) {
      found.push({
        substanceId: substanceId,
        results: matchingResults
      });
    }
  });

  return found;
}

function isResultMatching(result, query, isSimpleQuery) {
  // Match no reaction marker
  if (query === NO_REACTION) {
    return result[2] === false;

    // Match simple color palette
  } else if (isSimpleQuery) {
    return areColorsMatching(result[1], query);

    // Match full color palette
  } else {
    return areColorsMatching(result[0], query);
  }
}

function areColorsMatching(colorIds, queryColorIds) {
  let anyNotMatching = false;
  queryColorIds.forEach(colorId => {
    if (!colorIds.includes(colorId)) {
      anyNotMatching = true;
    }
  });
  return !anyNotMatching;
}
